import { NextPage } from 'next'

import { createContext, useEffect, useRef, useState } from 'react'

import Layout from '../../components/Layout'
import { t } from '../../utils/i18n/index'
import styles from './_artistsPage.module.scss'

import Switcher from '../../components/ui/SwitcherComponent'
import ListSort from '../../components/ui/ListSort'
import classnames from 'classnames'
import ArtistComponent from '../../components/ArtistComponent'
import SongComponent from '../../components/SongComponent'
import SortByLetters from '../../components/ui/SortByLetter'
import { useLazyQuery } from '@apollo/client'
import { useAuth } from '../../utils/auth'
import { staticProps } from '../../utils/createApolloClient'
import useInView from 'react-cool-inview'
import { ARTISTS_LIST_QUERY } from '../../gql/queries/artists_list_query'
import { SONGS_LIST_QUERY } from '../../gql/queries/songs_list_query'
import { Artist, Song } from '../../__generated__/graphql'

export const SearchContext = createContext(null)

export interface FiltersState {
  orderBy: string
  favorite: boolean
}

const initalFiltersState: FiltersState = {
  favorite: false,
  orderBy: 'CURRENTLY_TRENDING'
}

const FavoritesArtistsList = ({ artists, favoriteSongs }) => {
  const songsGroupedByArtists = (
    artists: Artist[],
    songs: Song[]
  ): { artist: Artist; favoriteSongs: Song[] }[] => {
    return artists.map((artist) => {
      return {
        artist,
        favoriteSongs: songs.filter((song) =>
          song.artists.find((a) => a.id === artist.id)
        )
      }
    })
  }

  return songsGroupedByArtists(artists, favoriteSongs)
    .filter(({ favoriteSongs }) => favoriteSongs.length > 0)
    .map(({ artist, favoriteSongs }) => {
      return (
        <div
          key={artist.id}
          className={classnames(
            'columns',
            styles.favoriteArtistColumn,
            styles.favoriteArtist
          )}
        >
          <div
            className={classnames(
              styles.favoritItem,
              'column is-full-mobile is-one-third-tablet is-one-third-desktop is-one-quarter-widescreen'
            )}
          >
            <ArtistComponent artist={artist} mode={'full'} />
          </div>
          <div className="column is-full-mobile is-two-thirds-tablet is-two-thirds-desktop is-three-quarters-widescreen">
            <div className={styles.favoriteSongs}>
              {favoriteSongs.map((favSong) => {
                return (
                  <SongComponent
                    key={favSong.id}
                    song={favSong}
                    isFlat={true}
                    inList={true}
                    hasTextScroll={true}
                  />
                )
              })}
            </div>
          </div>
        </div>
      )
    })
}

const DefaultArtistsList = ({ artists, observe = null }) => {
  let currentLetter = ''
  return artists.map((artist, index) => {
    const artistFirstLetter = artist.name.toUpperCase().charAt(0)

    if (currentLetter !== artistFirstLetter) {
      currentLetter = artistFirstLetter
    }

    const anchor = <a id={`letter-${currentLetter}`}></a>

    return (
      <div
        key={artist.id}
        className={classnames(
          styles.artistItem,
          'column is-half-mobile is-half-tablet is-one-third-desktop is-one-quarter-widescreen'
        )}
        ref={index == 8 ? observe : null} //render all elements after scrolling to 9th
      >
        {anchor}
        <ArtistComponent
          artist={artist}
          mode={'full'}
          priority={index >= 0 && index < 8}
        />
      </div>
    )
  })
}

const ArtistsPage: NextPage<{ ssrArtists: Artist[] }> = ({ ssrArtists }) => {
  const [filtersState, setFiltersState] =
    useState<FiltersState>(initalFiltersState)
  const [artists, setArtists] = useState<Artist[]>(ssrArtists || [])
  const [favoriteSongs, setFavoriteSongs] = useState<Song[]>([])
  const [isChecked, setIsChecked] = useState(false)
  const { currentUser } = useAuth()
  const initialRender = useRef(true)

  const [performSearch] = useLazyQuery(ARTISTS_LIST_QUERY, {
    onCompleted: (data) => {
      setArtists(data.artists as Artist[])
    }
  })

  const [searchFavoriteSongs] = useLazyQuery(SONGS_LIST_QUERY, {
    variables: { favorite: true },
    onCompleted: (data) => {
      if (data) setFavoriteSongs(data.songs as Song[])
    },
    fetchPolicy: 'no-cache'
  })

  const doSearch = (filterState) => {
    const variables = { orderBy: filterState.orderBy }
    performSearch({ variables })
  }

  useEffect(() => {
    if (currentUser) searchFavoriteSongs()
  }, [currentUser])

  useEffect(() => {
    if (!initialRender.current) doSearch(filtersState)
    else initialRender.current = false
  }, [filtersState, currentUser])

  const { observe, inView } = useInView({
    onEnter: ({ unobserve }) => unobserve()
  })

  const isAlphabeticalOrder = ['A_TO_Z', 'Z_TO_A'].includes(
    filtersState.orderBy
  )
  const content = () => {
    return (
      <div className={`${styles.allArtists} page-content`}>
        {!filtersState.favorite && isAlphabeticalOrder && (
          <SortByLetters
            reversed={filtersState.orderBy === 'Z_TO_A'}
            items={artists.map((el) => el.name)}
          />
        )}

        {!filtersState.favorite && (
          <div
            className={'columns is-variable is-multiline is-mobile is-3-fullhd'}
          >
            {isAlphabeticalOrder ? (
              DefaultArtistsList({ artists })
            ) : (
              <>
                {DefaultArtistsList({ artists: artists.slice(0, 13), observe })}
                {inView && DefaultArtistsList({ artists: artists.slice(13) })}
              </>
            )}
          </div>
        )}

        {filtersState.favorite &&
          FavoritesArtistsList({ artists, favoriteSongs })}
      </div>
    )
  }

  return (
    <Layout
      pageTitle={t('WorshipOnline - Artists')}
      pageDescription={t(
        'Video tutorials, lessons, chords and tabs (in any key) for over a hundred worship artists of all genres and styles.'
      )}
    >
      <SearchContext.Provider
        value={{
          filtersState: filtersState,
          setFiltersState: setFiltersState
        }}
      >
        <div
          className={classnames('columns is-gapless', styles.artistsContainer)}
        >
          <div className="container">
            <header className={`level ${styles.header}`}>
              <div className={`level-left ${styles.headerTitle}`}>
                <div className="level-item">
                  <h1>{t('All Artists')}</h1>
                </div>
              </div>
              <div className={`level-right ${styles.headerActions}`}>
                <ListSort SearchContext={SearchContext} />
                {currentUser && (
                  <Switcher
                    label={t('Favorites')}
                    onClick={() => {
                      setIsChecked(!isChecked)
                      setFiltersState((prevValue) => ({
                        ...prevValue,
                        favorite: !isChecked
                      }))
                    }}
                    isChecked={isChecked}
                  />
                )}
              </div>
            </header>
            {content()}
          </div>
        </div>
      </SearchContext.Provider>
    </Layout>
  )
}

export const getStaticProps = staticProps({
  additionalProps: async ({ client }) => {
    const {
      data: { artists: ssrArtists }
    } = await client.query({
      query: ARTISTS_LIST_QUERY,
      variables: initalFiltersState
    })

    return {
      ssrArtists
    }
  }
})

export default ArtistsPage
